// ============================================================
// useChannels (v8.363.3) - the GM's vox groups.
//
// A group is a named list of PEOPLE (uids), not a sheet team: players
// switch characters at will and the actor pointer is theirs to change.
// `seedFromTeam` turns a sheet team into one once, through the published
// actingAs - whoever is acting as a member now joins.
// ============================================================
import { useCallback, useEffect, useState } from 'react';
import { randomToken } from '../utils/entropy';
import type { ChatChannel } from '../engine/chat';
import type { CollectionRead } from '../backend/types';

interface ChannelsMp {
  onChannelsUpdate: (sessionId: string, cb: (read: CollectionRead<Record<string, unknown>>) => void) => () => void;
  setChannel: (sessionId: string, id: string, doc: Record<string, unknown>) => Promise<void>;
  deleteChannel: (sessionId: string, id: string) => Promise<void>;
}

interface Params {
  sessionId: string | null;
  me: string;
  mp: ChannelsMp;
  /** Injected so tests mint predictably. */
  mint?: () => string;
  now?: () => number;
}

interface NewChannel {
  name: string;
  members: string[];
  gmListens?: boolean;
  fromTeam?: string;
}

const text = (v: unknown) => (typeof v === 'string' ? v : '');
const defaultMint = () => `ch-${randomToken(6)}`;

function channelFromDoc(id: string, d: Record<string, unknown>): ChatChannel {
  return {
    id,
    name: text(d.name),
    members: Array.isArray(d.members) ? d.members.filter((m): m is string => typeof m === 'string') : [],
    gmListens: d.gmListens !== false,
    createdBy: text(d.createdBy),
    createdAt: typeof d.createdAt === 'number' ? d.createdAt : 0,
    fromTeam: text(d.fromTeam),
  };
}

const oldestFirst = (read: CollectionRead<Record<string, unknown>>) =>
  read.entries
    .map((e) => channelFromDoc(e.id, e.data))
    .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));

/** What a channel document holds - everything but its id. */
function channelDoc(c: Omit<ChatChannel, 'id'>): Record<string, unknown> {
  return {
    name: c.name,
    members: c.members,
    gmListens: c.gmListens,
    createdBy: c.createdBy,
    createdAt: c.createdAt,
    fromTeam: c.fromTeam,
  };
}

/** A new group: the GM listening unless told otherwise, members de-duplicated. */
function newChannel(input: NewChannel, me: string, at: number): Omit<ChatChannel, 'id'> {
  return {
    name: input.name.trim() || 'Vox group',
    members: [...new Set(input.members)],
    gmListens: input.gmListens !== false,
    createdBy: me,
    createdAt: at,
    fromTeam: input.fromTeam ?? '',
  };
}

/** The people acting as a team's members right now. */
function actingMembers(team: { members: { id: string }[] }, actingBy: Record<string, string>): string[] {
  const memberIds = new Set(team.members.map((m) => m.id));
  return Object.entries(actingBy)
    .filter(([, cid]) => memberIds.has(cid))
    .map(([uid]) => uid);
}

/** The session's groups, oldest first; none outside a session. */
function useChannelFeed(sessionId: string | null, mp: ChannelsMp) {
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  useEffect(() => {
    if (!sessionId) {
      setChannels([]);
      return;
    }
    return mp.onChannelsUpdate(sessionId, (read) => setChannels(oldestFirst(read)));
  }, [sessionId, mp]);
  return channels;
}

export default function useChannels({ sessionId, me, mp, mint = defaultMint, now = Date.now }: Params) {
  const channels = useChannelFeed(sessionId, mp);
  const create = useCallback(
    async (input: NewChannel): Promise<string | null> => {
      if (!sessionId) return null;
      const id = mint();
      await mp.setChannel(sessionId, id, channelDoc(newChannel(input, me, now())));
      return id;
    },
    [sessionId, me, mp, mint, now],
  );
  const { update, remove } = useChannelEdits(sessionId, channels, mp);
  /**
   * A group from a sheet team: the people acting as its members right now.
   * Returns the uids it found, so the dialog can say who was left out.
   */
  const seedFromTeam = useCallback(
    async (team: { tag: string; label: string; members: { id: string }[] }, actingBy: Record<string, string>) => {
      const uids = actingMembers(team, actingBy);
      const id = await create({ name: team.label, members: uids, fromTeam: team.tag });
      return { id, uids };
    },
    [create],
  );
  return { channels, create, update, remove, seedFromTeam };
}

/** Change a group's name, members or listening; delete one. */
function useChannelEdits(sessionId: string | null, channels: ChatChannel[], mp: ChannelsMp) {
  const update = useCallback(
    async (id: string, patch: Partial<Pick<ChatChannel, 'name' | 'members' | 'gmListens'>>) => {
      const cur = channels.find((c) => c.id === id);
      if (!sessionId || !cur) return;
      const next = { ...cur, ...patch, members: [...new Set(patch.members ?? cur.members)] };
      await mp.setChannel(sessionId, id, channelDoc(next));
    },
    [sessionId, channels, mp],
  );
  const remove = useCallback(
    async (id: string) => {
      if (sessionId) await mp.deleteChannel(sessionId, id);
    },
    [sessionId, mp],
  );
  return { update, remove };
}
